import React, { FC } from 'react'

import styled from 'styled-components'

import { colors, device } from 'src/Helpers/constants.ts'
import DefaultEquipment from 'src/Assets/equipment.svg?react'
import Trash from 'src/Assets/trash.svg?react'
import { getValueOrFallback, numberToMoney } from 'src/Helpers/functions.tsx'
import { IconButton } from 'src/Components/Button/IconButton.tsx'
import { Input } from 'src/Components/Input/index.tsx'
import { Checkbox } from 'src/Components/Checkbox/index.tsx'
import { HintTooltip } from 'src/Components/HintTooltip'
import {
  ModelCalculated,
  OfferModelUnit,
} from 'src/Pages/Calculator/useCalculator.tsx'
import {
  getOfferGalleryImages,
  getUnitsQuantity,
} from 'src/Pages/Calculator/helpers.ts'

import { UnitsList } from './UnitsList'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #f7f9fc;
  padding: 20px 20px;
  //gap: 30px;
`

const ImagesRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  max-width: 180px;
`

const GalleryHint = styled.div`
  margin-top: 4px;
  font-size: 10px;
  color: ${colors.thirdGray};
  max-width: 180px;
`

const StyledImage = styled.img<{ $selected?: boolean; $selectable?: boolean }>`
  border-radius: 5px;
  height: 50px;
  width: 50px;
  object-fit: cover;
  cursor: ${({ $selectable }) => ($selectable ? 'pointer' : 'default')};
  border: 2px solid
    ${({ $selected }) => ($selected ? colors.mainBlue : 'transparent')};
  opacity: ${({ $selected, $selectable }) =>
    $selectable && !$selected ? 0.6 : 1};
`
const ParametersContainer = styled.div`
  display: grid;
  grid-column-gap: 16px;
  grid-template-columns: 1.4fr 0.8fr 0.8fr 0.8fr 0.6fr 0.4fr 0.2fr;

  @media ${device.tabletMax} {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }
`

const Parameter = styled.span`
  font-size: 12px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  color: ${colors.thirdGray};
  font-weight: 600;
  justify-content: center;
  flex-shrink: 0;
`

const NameWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 20px;
`
const Name = styled.span`
  font-size: 12px;
  max-width: 200px;
  width: 100%;
  text-align: left;
  color: ${colors.thirdGray};
  font-weight: 600;
`

const DestinationWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
`

const StyledButton = styled(IconButton)`
  background-color: ${colors.red} !important;

  &:hover {
    background-color: ${colors.red} !important;
  }
  margin-left: auto;
`

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`

interface TransportCalcItemProps {
  onDeleteClick: () => void
  model: ModelCalculated
  onPriceChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onDetalizationChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onSelectImage: (url: string) => void
  onAddUnit: () => void
  onRemoveUnit: (unitIndex: number) => void
  onUpdateUnit: <T extends keyof OfferModelUnit>(
    unitIndex: number,
    property: T,
    value: OfferModelUnit[T],
  ) => void
}

export const TransportCalcItem: FC<TransportCalcItemProps> = ({
  model,
  onDeleteClick,
  onPriceChange,
  onDetalizationChange,
  onSelectImage,
  onAddUnit,
  onRemoveUnit,
  onUpdateUnit,
}) => {
  const { totalRentPricePerHour, newestEquipmentYear } = model

  const galleryImages = getOfferGalleryImages(model)
  const selectedImage = model.selectedImageUrl || model.imageUrl

  const formattedTools =
    model?.modelTools?.map((modelTool) => {
      return {
        label: modelTool?.name || '',
        value: String(modelTool?.id),
      }
    }) || []

  return (
    <Container data-testid='equipment-item'>
      <ParametersContainer>
        <NameWrapper>
          <div>
            {galleryImages.length > 0 ? (
              <ImagesRow>
                {galleryImages.map((url) => (
                  <StyledImage
                    key={url}
                    src={url}
                    $selectable={galleryImages.length > 1}
                    $selected={
                      galleryImages.length > 1 && url === selectedImage
                    }
                    onClick={() => onSelectImage(url)}
                  />
                ))}
              </ImagesRow>
            ) : (
              <DefaultEquipment width={50} height={50} />
            )}
            {galleryImages.length > 1 && (
              <GalleryHint>
                Нажмите на фото, чтобы выбрать его для КП
              </GalleryHint>
            )}
          </div>
          <Name>
            {model?.modelType?.label} {model.name}{' '}
            {getValueOrFallback(newestEquipmentYear)}
          </Name>
        </NameWrapper>

        <DestinationWrapper>
          <Parameter>
            <div>Кол-во техники:</div>
            <div>{getUnitsQuantity(model.units)}</div>
          </Parameter>
        </DestinationWrapper>

        <DestinationWrapper>
          <Parameter>
            <HintTooltip label='Ставка за машино-час, от которой считается КП. По умолчанию — базовая стоимость модели, для этого КП её можно изменить'>
              <Input
                type='money'
                label={'Стоимость аренды'}
                placeholder={'Стоимость аренды'}
                value={model.specialPrice?.toString() ?? ''}
                onChange={onPriceChange}
                data-testid='equipment-price'
              />
            </HintTooltip>
          </Parameter>
        </DestinationWrapper>

        <DestinationWrapper>
          <Parameter>
            <div>Базовая стоимость:</div>
            <div>{numberToMoney(model.baseRentalCost)}</div>
          </Parameter>
          <Parameter>
            <div>Стоимость за 1 ед.:</div>
            <div>{numberToMoney(totalRentPricePerHour)}</div>
          </Parameter>
        </DestinationWrapper>

        <HintTooltip label='Добавить в КП таблицу с разбивкой стоимости этой техники: работа, навесное, доставка запчастей, персонал, топливо'>
          <Parameter>
            Детализация
            <Checkbox
              checked={model.detalization || false}
              onChange={onDetalizationChange}
              data-testid='equipment-detalization'
            />
          </Parameter>
        </HintTooltip>

        <ButtonContainer>
          <StyledButton onClick={onDeleteClick} tooltiptext={'Удалить технику'}>
            <Trash />
          </StyledButton>
        </ButtonContainer>
      </ParametersContainer>

      {/* Список единиц техники */}
      <UnitsList
        units={model.units || []}
        tools={formattedTools}
        onAddUnit={onAddUnit}
        onRemoveUnit={onRemoveUnit}
        onUpdateUnit={onUpdateUnit}
      />
    </Container>
  )
}
